import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseRequest, TemplateMessageObject, TemplateMessageObjectPager, TemplateMessageRequest } from '@trendbuild/trend-cloud-api';
import { catchError, map, Observable, shareReplay, tap, throwError } from 'rxjs';
import { AbstractService } from '../commons/abstract.service';
import { Channel, ChannelAPIs, ChannelEmbedded, HybridOfficialShare, HybridSessionPairing, VoipExtension, VoipExtensionCredentials } from '../models/channel.model';
import { WaTemplateDefinition, WaTemplateEditDefinition, WaTemplateMediaHandle } from '../models/wa-template-definition.model';

@Injectable({ providedIn: 'root' })
export class ChannelService extends AbstractService<Channel> {

  constructor(private http: HttpClient) {
    super(http);
  }

  getURLBase(): string {
    return '/channel';
  }

  preCreate(obj: Channel): void {
    delete obj?._id;
  }
  preUpdate(obj: Channel): void {
  }

  createFromEmbedded(idChannel: string, obj: ChannelEmbedded): Observable<Channel> {
    return super.post(`/createFromEmbedded/${idChannel}`, obj);
  }

  actived(id: string): Observable<Channel> {
    return super.get(`/actived/${id}`);
  }

  unactived(id: string): Observable<Channel> {
    return super.get(`/unactived/${id}`);
  }

  getInstanceConnect(id: string): Observable<unknown> {
    return super.get(`/get/instance/connect/${id}`);
  }

  syncInstanceByChannel(id: string): Observable<Channel> {
    return super.get(`/sync/instance/${id}`);
  }

  restartInstanceByChannel(id: string): Observable<Channel> {
    return super.get(`/restart/instance/${id}`);
  }

  disconnectInstanceByChannel(id: string): Observable<Channel> {
    return super.get(`/disconnect/instance/${id}`);
  }

  /** Creates (if absent) the session side of a Hybrid channel and returns its pairing QR (RF-02). */
  pairHybridSession(idChannel: string): Observable<HybridSessionPairing> {
    return super.post(`/hybrid/${idChannel}/session`, {});
  }

  /** Unpairs the session side; the channel keeps working as official-only (RF-06). */
  unpairHybridSession(idChannel: string): Observable<Channel> {
    return super.deleteWithParams(`/hybrid/${idChannel}/session`);
  }

  /** Sets the share of free-form messages carried by the official side (RF-26). */
  updateHybridShare(idChannel: string, officialShare: HybridOfficialShare): Observable<Channel> {
    return super.put(`/hybrid/${idChannel}/share`, { officialShare });
  }

  syncContactsByChannel(id: string): Observable<Channel> {
    return super.get(`/syncContacts/instance/${id}`);
  }

  getByAllChannel(): Observable<Channel[]> {
    return super.get(`/list`);
  }

  override getList(name?: string): Observable<Channel[]> {
    const params = name ? { params: { name } } : {};
    return this.http.get<Channel[]>(this.getURLBase() + '/list', params);
  }

  getByIdAndMetrics(id: string, phoneNumber: string): Observable<Array<{ sentMessages: number, receivedMessages: number, totalAttendance: number, totalMessages: number, totalContacts: number }>> {
    return super.get(`/byId/${id}?phoneNumber=${phoneNumber}`);
  }

  /**
   * Template listings already read in this session, keyed by channel and status.
   *
   * Listing templates is the slow part of every template screen: the API has no company-wide
   * endpoint, so it asks Meta for one WhatsApp Business Account at a time, and on a cache miss it
   * pages through the Graph API inside the request. The API keeps the answer in Redis with no
   * expiry, so the second read is already cheap — this map removes even that round trip, which is
   * what makes selecting a channel that was already opened instant instead of merely fast.
   *
   * The map holds the *request*, not the result, so callers that ask for the same listing while it
   * is still in flight share one call instead of each starting their own — the webhook-integration
   * form renders one template picker per item, and they all point at the same channel.
   *
   * Every write below drops the channel's entries, and so does
   * `cleanCacheTemplatesByChannel` — the "Atualizar" button, which is also the way out when
   * another session changed a template.
   */
  private readonly templatesCache = new Map<string, Observable<TemplateMessageObjectPager>>();

  private templatesCacheKey(idChannel: string, status?: string): string {
    return `${idChannel}:${status ?? ''}`;
  }

  private forgetTemplates(idChannel: string): void {
    for (const key of Array.from(this.templatesCache.keys())) {
      if (key.startsWith(`${idChannel}:`)) {
        this.templatesCache.delete(key);
      }
    }
  }

  private cloneTemplates(pager: TemplateMessageObjectPager): TemplateMessageObjectPager {
    return { ...pager, data: [...(pager.data ?? [])] };
  }

  getTemplatesByChannel(id: string, status?: string): Observable<TemplateMessageObjectPager> {
    const cacheKey = this.templatesCacheKey(id, status);
    const cached = this.templatesCache.get(cacheKey);
    if (!!cached) {
      return cached.pipe(map((pager) => this.cloneTemplates(pager)));
    }

    const request: Observable<TemplateMessageObjectPager> = !!status
      ? super.getWithParams(`/get/all/templates/${id}`, { status })
      : super.get(`/get/all/templates/${id}`);

    let shared: Observable<TemplateMessageObjectPager>;
    const forgetIfStillMine = () => {
      if (this.templatesCache.get(cacheKey) === shared) {
        this.templatesCache.delete(cacheKey);
      }
    };

    shared = request.pipe(
      tap((pager) => {
        if (!pager?.data?.length) {
          forgetIfStillMine();
        }
      }),
      catchError((error) => {
        forgetIfStillMine();
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.templatesCache.set(cacheKey, shared);
    return shared.pipe(map((pager) => this.cloneTemplates(pager)));
  }

  cleanCacheTemplatesByChannel(id: string): Observable<void> {
    return super.get(`/cleanCache/templates/${id}`)
      .pipe(tap(() => this.forgetTemplates(id)));
  }

  getTemplateByChannel(idChannel: string, idTemplate: string): Observable<TemplateMessageObject> {
    return super.get(`/templates/${idChannel}/${idTemplate}`);
  }

  createTemplateByChannel(idChannel: string, template: WaTemplateDefinition): Observable<{ id?: string, status?: string, category?: string }> {
    return super.post(`/templates/${idChannel}`, template)
      .pipe(tap(() => this.forgetTemplates(idChannel)));
  }

  updateTemplateByChannel(idChannel: string, idTemplate: string, template: WaTemplateEditDefinition): Observable<{ success?: boolean }> {
    return super.put(`/templates/${idChannel}/${idTemplate}`, template)
      .pipe(tap(() => this.forgetTemplates(idChannel)));
  }

  deleteTemplateByChannel(idChannel: string, idTemplate: string, name?: string): Observable<{ success?: boolean }> {
    return super.deleteWithParams(`/templates/${idChannel}/${idTemplate}`, name ? { name } : {})
      .pipe(tap(() => this.forgetTemplates(idChannel)));
  }

  unpauseTemplateByChannel(idChannel: string, idTemplate: string): Observable<{ success?: boolean }> {
    return super.post(`/templates/${idChannel}/${idTemplate}/unpause`, {})
      .pipe(tap(() => this.forgetTemplates(idChannel)));
  }

  /**
   * Sends a template message through the channel. The provider credentials stay on the server —
   * the browser only says what to send and to whom.
   *
   * `templateCategory` is the category Meta reports for the template being sent. The request names
   * the template but does not carry its category, and on a credit-line channel the category is what
   * the send is charged under — left out, the movement is recorded unpriced. It travels beside the
   * provider payload, never inside it: the API takes it off before calling Meta.
   */
  sendTemplateMessageByChannel(idChannel: string, message: Omit<TemplateMessageRequest, keyof BaseRequest>, templateCategory?: string): Observable<unknown> {
    const body = templateCategory ? { ...message, templateCategory } : message;
    return super.post(`/templates/${idChannel}/send`, body);
  }

  /**
   * Uploads the example media of a template header and returns the handle Meta expects
   * when the template is created.
   */
  uploadTemplateMediaByChannel(idChannel: string, file: File): Observable<WaTemplateMediaHandle> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return super.post(`/templates/${idChannel}/media`, formData);
  }

  /**
   * Connects the Instagram accounts of a Meta authorization. An account already registered as a
   * channel has its connection data refreshed, so an expired token is fixed without deleting the
   * channel and losing its conversation history.
   *
   * `idChannel` comes from the reconnect action on a channel page and restricts the call to that
   * channel — without it, every authorized page is connected.
   */
  createInstagramChannel(token: string, idChannel?: string): Observable<Channel[]> {
    return super.post(`/createInstagramChannel`, { token, idChannel });
  }

  generateNewAPITokenChannel(idChannel: string): Observable<ChannelAPIs> {
    return super.get(`/generate/new/api/token/${idChannel}`);
  }

  /** See createInstagramChannel — same connect/reconnect contract for Messenger. */
  createMessengerChannel(token: string, idChannel?: string): Observable<Channel[]> {
    return super.post(`/createMessengerChannel`, { token, idChannel });
  }

  getCountTotalChannels(idCompany: string): Observable<number> {
    return super.get(`/total/${idCompany}`);
  }

  getCallVoipToken(idChannel: string): Observable<{ token: string }> {
    return super.get(`/token/${idChannel}`);
  }

  listZAPIIncomingEnabled(): Observable<Array<{ _id: string; instanceId: string }>> {
    return super.get('/get/list-zapi-incoming-enabled');
  }

  // VoIP extensions — managed on the VOIP_API channel (keyed by sipUsername)
  listExtensions(idChannel: string): Observable<VoipExtension[]> {
    return super.get(`/${idChannel}/extensions`);
  }

  addExtension(idChannel: string, extension: VoipExtension): Observable<VoipExtension> {
    return super.post(`/${idChannel}/extensions`, extension);
  }

  updateExtension(idChannel: string, sipUsername: string, extension: VoipExtension): Observable<VoipExtension> {
    return super.put(`/${idChannel}/extensions/${sipUsername}`, extension);
  }

  removeExtension(idChannel: string, sipUsername: string): Observable<void> {
    // AbstractService.delete builds `${base}/${id}`, so pass the suffix without a leading slash
    return super.delete(`${idChannel}/extensions/${sipUsername}`);
  }

  // Inbound route (setInbound): the ramal the trunk DID rings
  setDefaultExtension(idChannel: string, sipUsername: string): Observable<Channel> {
    return super.put(`/${idChannel}/inbound`, { sipUsername });
  }

  getMyExtensionCredentials(idChannel: string): Observable<VoipExtensionCredentials> {
    return super.get(`/${idChannel}/extensions/me/credentials`);
  }

  // Force-push the stored SIP secret back to the PBX and reload — repairs a ramal whose softphone loops on 401
  resyncExtension(idChannel: string, sipUsername: string): Observable<VoipExtension> {
    return super.post(`/${idChannel}/extensions/${sipUsername}/resync`, {});
  }

}
